/** SI energy/power labels that keep the displayed mantissa under 1,000. */

export const ENERGY_UNITS = ['kWh', 'MWh', 'GWh', 'TWh'];
export const POWER_UNITS = ['kW', 'MW', 'GW', 'TW'];
export const UNIT_CAP = 1000;

export function scaleByThousands(value, units, cap = UNIT_CAP) {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    let scaled = n;
    let unitIndex = 0;
    while (Math.abs(scaled) >= cap && unitIndex < units.length - 1) {
        scaled /= cap;
        unitIndex += 1;
    }
    return { value: scaled, unit: units[unitIndex], factor: cap ** unitIndex };
}

export function formatMantissa(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    const abs = Math.abs(n);
    const digits = abs >= 10 ? 1 : 2;
    const rounded = Number(n.toFixed(digits));
    return rounded.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
    });
}

export function formatScaledSi(value, units, { suffix = '', empty = '—' } = {}) {
    const scaled = scaleByThousands(value, units);
    if (!scaled) return empty;
    let mantissa = scaled.value;
    let unit = scaled.unit;
    const digits = Math.abs(mantissa) >= 10 ? 1 : 2;
    if (Math.abs(Number(mantissa.toFixed(digits))) >= UNIT_CAP) {
        const next = units[units.indexOf(unit) + 1];
        if (next) {
            mantissa /= UNIT_CAP;
            unit = next;
        }
    }
    return `${formatMantissa(mantissa)} ${unit}${suffix}`;
}

export function formatEnergyKwh(kwh, options) {
    return formatScaledSi(kwh, ENERGY_UNITS, options);
}

export function formatPowerKw(kw, options) {
    return formatScaledSi(kw, POWER_UNITS, options);
}

export function scaleAxisFromValues(values, units) {
    const nums = (Array.isArray(values) ? values : []).map((v) => Number(v) || 0);
    const max = nums.reduce((hi, n) => Math.max(hi, Math.abs(n)), 0);
    const scaled = scaleByThousands(max, units) || { unit: units[0], factor: 1 };
    return {
        unit: scaled.unit,
        factor: scaled.factor,
        formatTick: (raw) => formatMantissa((Number(raw) || 0) / scaled.factor),
    };
}

export function energyScaleFromKwh(values) {
    return scaleAxisFromValues(values, ENERGY_UNITS);
}

export function powerScaleFromKw(values) {
    return scaleAxisFromValues(values, POWER_UNITS);
}

export function applyEnergyUnitLabel(label, unit) {
    const text = String(label || '').trim();
    if (!text || /^(kWh|MWh|GWh|TWh)$/i.test(text)) return unit;
    if (/kWh\s*\/\s*day/i.test(text) || /\/\s*day\s*$/i.test(text)) {
        const base = text
            .replace(/\s*\((?:kWh|MWh|GWh|TWh)(?:\s*\/\s*day)?\)\s*$/i, '')
            .replace(/\s*\/\s*day\s*$/i, '')
            .trim();
        return base ? `${base} (${unit} / day)` : `${unit} / day`;
    }
    const base = text.replace(/\s*\((?:kWh|MWh|GWh|TWh)\)\s*$/i, '').trim();
    return base ? `${base} (${unit})` : unit;
}

export function applyPowerUnitLabel(label, unit) {
    const text = String(label || '').trim();
    if (!text || /^(kW|MW|GW|TW)$/i.test(text)) return unit;
    const base = text
        .replace(/\s*\((?:kW|MW|GW|TW|kWh|MWh|GWh|TWh)(?:\s*\/\s*(?:hr|hour))?\)\s*$/i, '')
        .replace(/\s*\/\s*(?:hr|hour)\s*$/i, '')
        .trim();
    return base ? `${base} (${unit})` : unit;
}
